import React, { useEffect, useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Plus, 
  Search, 
  Filter,
  FileText, 
  CreditCard, 
  Shield,
  CalendarIcon,
  MoreVertical,
  Pencil,
  Trash2,
  RefreshCw,
  CheckCircle,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Switch } from '@/components/ui/switch';
import axios from 'axios';
import { API, useAuth } from '@/App';
import { format, parseISO, differenceInDays, setMonth, setYear, getMonth, getYear } from 'date-fns';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const RenewalsPage = () => {
  const { token } = useAuth();
  const [renewals, setRenewals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRenewal, setEditingRenewal] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    category: 'document',
    description: '',
    renewal_date: new Date(),
    reminder_days: [7, 3, 1],
    notes: '',
    is_recurring: false,
    recurrence_period: 12
  });

  const fetchRenewals = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const params = categoryFilter !== 'all' ? { category: categoryFilter } : {};
      const response = await axios.get(`${API}/renewals`, { 
        headers, 
        params,
        withCredentials: true 
      });
      setRenewals(response.data);
    } catch (error) {
      console.error('Error fetching renewals:', error);
      toast.error('Failed to load renewals');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRenewals();
  }, [token, categoryFilter]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const payload = {
        ...formData,
        renewal_date: format(formData.renewal_date, 'yyyy-MM-dd')
      };

      if (editingRenewal) {
        await axios.put(`${API}/renewals/${editingRenewal.renewal_id}`, payload, {
          headers,
          withCredentials: true
        });
        toast.success('Renewal updated successfully');
      } else {
        await axios.post(`${API}/renewals`, payload, {
          headers,
          withCredentials: true
        });
        toast.success('Renewal created successfully');
      }

      setIsModalOpen(false);
      resetForm();
      fetchRenewals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save renewal');
    }
  };

  const handleDelete = async (renewalId) => {
    if (!confirm('Are you sure you want to delete this renewal?')) return;
    
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.delete(`${API}/renewals/${renewalId}`, {
        headers,
        withCredentials: true
      });
      toast.success('Renewal deleted');
      fetchRenewals();
    } catch (error) {
      toast.error('Failed to delete renewal');
    }
  };

  const handleEdit = (renewal) => {
    setEditingRenewal(renewal);
    setFormData({
      title: renewal.title,
      category: renewal.category,
      description: renewal.description || '',
      renewal_date: parseISO(renewal.renewal_date),
      reminder_days: renewal.reminder_days,
      notes: renewal.notes || '',
      is_recurring: renewal.is_recurring || false,
      recurrence_period: renewal.recurrence_period || 12
    });
    setIsModalOpen(true);
  };

  const resetForm = () => {
    setEditingRenewal(null);
    setFormData({
      title: '',
      category: 'document',
      description: '',
      renewal_date: new Date(),
      reminder_days: [7, 3, 1],
      notes: '',
      is_recurring: false,
      recurrence_period: 12
    });
  };

  const handleMarkAsRenewed = async (renewal) => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.put(`${API}/renewals/${renewal.renewal_id}`, 
        { status: 'renewed' },
        { headers, withCredentials: true }
      );
      if (renewal.is_recurring) {
        toast.success('Marked as renewed! Next renewal created automatically.');
      } else {
        toast.success('Marked as renewed!');
      }
      fetchRenewals();
    } catch (error) {
      toast.error('Failed to update renewal');
    }
  };

  const openNewModal = () => {
    resetForm();
    setIsModalOpen(true);
  };

  const getStatusBadge = (renewalDate) => {
    const days = differenceInDays(parseISO(renewalDate), new Date());
    if (days < 0) return <Badge variant="destructive">Expired</Badge>;
    if (days <= 7) return <Badge className="bg-red-100 text-red-700 hover:bg-red-100">Due in {days} days</Badge>;
    if (days <= 30) return <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">Due in {days} days</Badge>;
    return <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">{days} days left</Badge>;
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'document': return <FileText className="w-4 h-4" />;
      case 'subscription': return <CreditCard className="w-4 h-4" />;
      case 'insurance': return <Shield className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  const filteredRenewals = renewals.filter(r => 
    r.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    r.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8" data-testid="renewals-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-ink font-display">Renewals</h1>
            <p className="text-ink-600 mt-1">Manage all your renewal reminders</p>
          </div>
          <Button 
            className="bg-ink hover:bg-black text-white"
            onClick={openNewModal}
            data-testid="new-renewal-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Renewal
          </Button>
        </div>

        {/* Filters */}
        <Card className="bg-white border border-ink-200 shadow-sm mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-600/70" />
                <Input
                  placeholder="Search renewals..."
                  className="pl-10 border-ink-200"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  data-testid="search-renewals-input"
                />
              </div>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-full sm:w-48 border-ink-200" data-testid="category-filter">
                  <Filter className="w-4 h-4 mr-2 text-ink-600/70" />
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="document">Documents</SelectItem>
                  <SelectItem value="subscription">Subscriptions</SelectItem>
                  <SelectItem value="insurance">Insurance</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Renewals List */}
        <Card className="bg-white border border-ink-200 shadow-sm">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ink mx-auto"></div>
              </div>
            ) : filteredRenewals.length === 0 ? (
              <div className="p-8 text-center text-ink-600">
                <FileText className="w-12 h-12 mx-auto mb-3 text-ink-600/50" />
                <p className="font-medium">No renewals found</p>
                <p className="text-sm mt-1">Create your first renewal to get started</p>
                <Button 
                  className="mt-4 bg-ink hover:bg-black text-white"
                  onClick={openNewModal}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Renewal
                </Button>
              </div>
            ) : (
              <div className="divide-y divide-ink-200">
                {filteredRenewals.map((renewal) => (
                  <div 
                    key={renewal.renewal_id} 
                    className="p-4 hover:bg-paper transition-colors"
                    data-testid={`renewal-item-${renewal.renewal_id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-md flex items-center justify-center ${
                          renewal.category === 'document' ? 'bg-cream text-respondent' :
                          renewal.category === 'subscription' ? 'bg-emerald-50 text-emerald-600' :
                          'bg-amber-50 text-amber-600'
                        }`}>
                          {getCategoryIcon(renewal.category)}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-ink">{renewal.title}</p>
                            {renewal.is_recurring && (
                              <Badge className="bg-cream text-respondent hover:bg-cream text-xs">
                                <RefreshCw className="w-3 h-3 mr-1" />
                                Recurring
                              </Badge>
                            )}
                            {renewal.status === 'renewed' && (
                              <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-xs">
                                Renewed
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-sm text-ink-600 capitalize">{renewal.category}</span>
                            {renewal.description && (
                              <>
                                <span className="text-ink-600/50">•</span>
                                <span className="text-sm text-ink-600 truncate max-w-[200px]">
                                  {renewal.description}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right hidden sm:block">
                          {getStatusBadge(renewal.renewal_date)}
                          <p className="text-sm text-ink-600 mt-1">
                            {format(parseISO(renewal.renewal_date), 'MMM d, yyyy')}
                          </p>
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`renewal-menu-${renewal.renewal_id}`}>
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {renewal.status !== 'renewed' && (
                              <DropdownMenuItem onClick={() => handleMarkAsRenewed(renewal)}>
                                <CheckCircle className="w-4 h-4 mr-2 text-emerald-600" />
                                Mark as Renewed
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => handleEdit(renewal)}>
                              <Pencil className="w-4 h-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              className="text-red-600 focus:text-red-600"
                              onClick={() => handleDelete(renewal.renewal_id)}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                    {/* Mobile status */}
                    <div className="mt-3 flex items-center justify-between sm:hidden">
                      {getStatusBadge(renewal.renewal_date)}
                      <span className="text-sm text-ink-600">
                        {format(parseISO(renewal.renewal_date), 'MMM d, yyyy')}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Add/Edit Modal */}
        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="font-display">
                {editingRenewal ? 'Edit Renewal' : 'New Renewal'}
              </DialogTitle>
              <DialogDescription>
                {editingRenewal ? 'Update the details for this renewal reminder.' : 'Add a new renewal reminder to track.'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Title</Label>
                  <Input
                    id="title"
                    placeholder="e.g., Passport Renewal"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                    data-testid="renewal-title-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="category">Category</Label>
                  <Select 
                    value={formData.category} 
                    onValueChange={(value) => setFormData({ ...formData, category: value })}
                  >
                    <SelectTrigger data-testid="renewal-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="document">Document</SelectItem>
                      <SelectItem value="subscription">Subscription</SelectItem>
                      <SelectItem value="insurance">Insurance</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Renewal Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !formData.renewal_date && "text-muted-foreground"
                        )}
                        data-testid="renewal-date-picker"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.renewal_date ? format(formData.renewal_date, "MMMM d, yyyy") : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <div className="p-3 border-b border-ink-200">
                        <div className="flex items-center justify-between gap-2">
                          <Select
                            value={String(getMonth(formData.renewal_date))}
                            onValueChange={(value) => {
                              const newDate = setMonth(formData.renewal_date, parseInt(value));
                              setFormData({ ...formData, renewal_date: newDate });
                            }}
                          >
                            <SelectTrigger className="w-[130px] h-9" data-testid="renewal-month-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {['January', 'February', 'March', 'April', 'May', 'June', 
                                'July', 'August', 'September', 'October', 'November', 'December'
                              ].map((month, index) => (
                                <SelectItem key={month} value={String(index)}>{month}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Select
                            value={String(getYear(formData.renewal_date))}
                            onValueChange={(value) => {
                              const newDate = setYear(formData.renewal_date, parseInt(value));
                              setFormData({ ...formData, renewal_date: newDate });
                            }}
                          >
                            <SelectTrigger className="w-[100px] h-9" data-testid="renewal-year-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {Array.from({ length: 20 }, (_, i) => {
                                const year = new Date().getFullYear() + i - 2;
                                return (
                                  <SelectItem key={year} value={String(year)}>{year}</SelectItem>
                                );
                              })}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <Calendar
                        mode="single"
                        selected={formData.renewal_date}
                        onSelect={(date) => setFormData({ ...formData, renewal_date: date || new Date() })}
                        month={formData.renewal_date}
                        onMonthChange={(date) => setFormData({ ...formData, renewal_date: date })}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description (optional)</Label>
                  <Input
                    id="description"
                    placeholder="Brief description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    data-testid="renewal-description-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notes">Notes (optional)</Label>
                  <Textarea
                    id="notes"
                    placeholder="Additional notes..."
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                    data-testid="renewal-notes-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Reminder Days Before</Label>
                  <div className="flex flex-wrap gap-2">
                    {[1, 3, 7, 14, 30].map((day) => (
                      <Button
                        key={day}
                        type="button"
                        variant={formData.reminder_days.includes(day) ? "default" : "outline"}
                        size="sm"
                        className={formData.reminder_days.includes(day) 
                          ? "bg-ink hover:bg-black" 
                          : "hover:bg-cream hover:text-ink"
                        }
                        onClick={() => {
                          const newDays = formData.reminder_days.includes(day)
                            ? formData.reminder_days.filter(d => d !== day)
                            : [...formData.reminder_days, day].sort((a, b) => b - a);
                          setFormData({ ...formData, reminder_days: newDays });
                        }}
                      >
                        {day} day{day > 1 ? 's' : ''}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Recurring Renewal */}
                <div className="space-y-4 pt-2 border-t border-ink-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-ink">Recurring Renewal</Label>
                      <p className="text-sm text-ink-600">Auto-create next reminder when marked as renewed</p>
                    </div>
                    <Switch
                      checked={formData.is_recurring}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_recurring: checked })}
                      data-testid="recurring-toggle"
                    />
                  </div>
                  {formData.is_recurring && (
                    <div className="space-y-2">
                      <Label>Recurrence Period</Label>
                      <Select 
                        value={String(formData.recurrence_period)} 
                        onValueChange={(value) => setFormData({ ...formData, recurrence_period: parseInt(value) })}
                      >
                        <SelectTrigger className="border-ink-200" data-testid="recurrence-period-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">1 month</SelectItem>
                          <SelectItem value="3">3 months</SelectItem>
                          <SelectItem value="6">6 months</SelectItem>
                          <SelectItem value="12">1 year</SelectItem>
                          <SelectItem value="24">2 years</SelectItem>
                          <SelectItem value="60">5 years</SelectItem>
                          <SelectItem value="120">10 years</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  className="bg-ink hover:bg-black text-white"
                  data-testid="renewal-submit-btn"
                >
                  {editingRenewal ? 'Update' : 'Create'} Renewal
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default RenewalsPage;
